import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { apiErrorText, useGetFieldInfoQuery, useGetFieldSitesQuery } from '@shared/api';
import { fmtFieldDate, siteReadOnly, siteStatusLabel, siteStatusTone } from '@entities/field';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Modules = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const ModuleChip = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const SiteCard = styled(Card).attrs({ as: Link })`
  display: block;
  text-decoration: none;
  color: inherit;
  transition:
    border-color 0.12s ease,
    transform 0.12s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-1px);
  }
`;

const SiteHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const SiteName = styled.div`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const SiteMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(2)};
  line-height: 1.5;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

/**
 * Обзор агента ОСНОВА.Прораб (`/agents/field`): перечень модулей агента + список
 * площадок (read-only, для офиса). Данные собираются прорабами в мобильном
 * приложении.
 */
export function FieldDashboard() {
  const { setActiveProject } = useActiveProjectContext();
  const { data: info } = useGetFieldInfoQuery();
  const { data: sitesResp, isLoading, isError, error } = useGetFieldSitesQuery();
  const sites = sitesResp?.sites ?? [];

  return (
    <>
      {info && info.modules.length > 0 && (
        <Section>
          <SectionLabel>Возможности агента</SectionLabel>
          <Modules>
            {info.modules.map((m) => (
              <ModuleChip key={m.code}>{m.title}</ModuleChip>
            ))}
          </Modules>
        </Section>
      )}

      <Section>
        <SectionLabel>Мои площадки и фронты работ</SectionLabel>
        {isLoading && <Muted>Загрузка площадок…</Muted>}
        {isError && <Muted>{apiErrorText(error, 'Не удалось загрузить площадки.')}</Muted>}
        {!isLoading && !isError && sites.length === 0 && (
          <Muted>Площадок пока нет. Их заводят ПТО/администратор для привязки к проекту.</Muted>
        )}
        {sites.length > 0 && (
          <Grid>
            {sites.map((s) => (
              <SiteCard
                key={s.id}
                to={`/agents/field/${s.id}`}
                onClick={() => {
                  if (s.project_id) {
                    setActiveProject(s.project_id, { name: s.name, code: s.code });
                  }
                }}
              >
                <SiteHead>
                  <SiteName>{s.name}</SiteName>
                  {siteReadOnly(s) ? (
                    <Badge
                      $tone="warning"
                      title="Проект ПТО в корзине — площадка только для чтения до восстановления проекта"
                    >
                      только чтение
                    </Badge>
                  ) : (
                    <Badge $tone={siteStatusTone(s.status)}>{siteStatusLabel(s.status)}</Badge>
                  )}
                </SiteHead>
                <SiteMeta>
                  {s.code && (
                    <>
                      Шифр: {s.code}
                      <br />
                    </>
                  )}
                  {s.location && (
                    <>
                      {s.location}
                      <br />
                    </>
                  )}
                  Создана: {fmtFieldDate(s.created_at)}
                </SiteMeta>
              </SiteCard>
            ))}
          </Grid>
        )}
      </Section>
    </>
  );
}
